import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

const SuperAdminNavigation: React.FC = () => {
  const location = useLocation();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/super-admin/dashboard',
      icon: HomeIcon,
      description: 'System overview and metrics'
    },
    {
      name: 'User Management',
      href: '/super-admin/users',
      icon: UsersIcon,
      description: 'Manage users across all tenants'
    },
    {
      name: 'Tenant Management',
      href: '/super-admin/tenants',
      icon: BuildingOfficeIcon,
      description: 'Manage tenant accounts and settings'
    },
    {
      name: 'Permissions',
      href: '/super-admin/permissions',
      icon: ShieldCheckIcon,
      description: 'Permission templates and overrides',
      children: [
        { name: 'Templates', href: '/super-admin/permissions/templates' },
        { name: 'Matrix View', href: '/super-admin/permissions/matrix' }
      ]
    },
    {
      name: 'Bulk Operations',
      href: '/super-admin/bulk',
      icon: ClipboardDocumentListIcon,
      description: 'Bulk user and tenant operations'
    },
    {
      name: 'Audit & Reports',
      href: '/super-admin/audit',
      icon: DocumentTextIcon,
      description: 'System audit logs and reports',
      children: [
        { name: 'Audit Logs', href: '/super-admin/audit/logs' },
        { name: 'Reports', href: '/super-admin/audit/reports' },
        { name: 'Security Alerts', href: '/super-admin/audit/security' }
      ]
    },
    {
      name: 'Analytics',
      href: '/super-admin/analytics',
      icon: ChartBarIcon,
      description: 'System-wide analytics and insights'
    },
    {
      name: 'System Settings',
      href: '/super-admin/settings',
      icon: Cog6ToothIcon,
      description: 'Global system configuration'
    }
  ];

  const isActiveRoute = (href: string) => {
    if (href === '/super-admin/dashboard') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const hasActiveChild = (item: any) => {
    return item.children?.some((child: any) => location.pathname.startsWith(child.href));
  };

  return (
    <nav className="mt-8 px-4">
      <ul className="space-y-2">
        {navigation.map((item) => {
          const isActive = isActiveRoute(item.href);
          const hasActiveChildRoute = hasActiveChild(item);
          const showChildren = item.children && (isActive || hasActiveChildRoute);

          return (
            <li key={item.name}>
              <Link
                to={item.href}
                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive || hasActiveChildRoute
                    ? 'bg-purple-100 text-purple-700 border-r-2 border-purple-500'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                title={item.description}
              >
                <item.icon 
                  className={`mr-3 h-5 w-5 flex-shrink-0 ${
                    isActive || hasActiveChildRoute ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`} 
                />
                {item.name}
                {item.children && (
                  <svg
                    className={`ml-auto h-4 w-4 transition-transform ${
                      showChildren ? 'rotate-90' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                )}
              </Link>
              
              {/* Sub-navigation */}
              {showChildren && (
                <ul className="mt-2 ml-8 space-y-1">
                  {item.children?.map((child) => (
                    <li key={child.name}>
                      <Link
                        to={child.href}
                        className={`block px-3 py-1.5 text-sm rounded-md transition-colors ${
                          location.pathname === child.href
                            ? 'bg-purple-50 text-purple-600 font-medium'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                        }`}
                      >
                        {child.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default SuperAdminNavigation;